<template>
  <v-container fluid transition="fade-transition">
    <confirmation-dialog
      :isConfirmationDialogOpened="isConfirmationDialogOpened"
      :action="confirmationDialogAction"
      @closeConfirmationDialog="closeConfirmationDialog"
      @actionConfirmed="duplicateConfirmed"
    >
    </confirmation-dialog>

    <confirm-delete-dialog :isDialogOpened="isDeleteDialogOpened" :itemNames="selectedTemplateType.name" @confirmDelete="deleteConfirmed" @closeDialog="closeDeleteDialog">
    </confirm-delete-dialog>

    <v-toolbar flat class="site-table--header site-table--lists">
      <v-row class="mt-n3 mb-0">
        <v-toolbar-title class="headline grey--text text--darken-3 ml-n3 pt-1">Template Types</v-toolbar-title>
        <v-spacer />
        <v-col cols="12" sm="5" md="4" lg="4" xl="3" class="text-center">
          <v-text-field v-model="search" label="Search" class="mt-n3" append-icon="mdi-magnify" outlined dense hide-details></v-text-field>
        </v-col>
        <v-btn color="blue" class="white--text ml-2 mr-n1" min-width="200" @click="addNewTemplateTypeDialog()"><v-icon left>mdi-plus-circle</v-icon>Add New Template Type</v-btn>
      </v-row>
    </v-toolbar>
    <div class="site-table mt-n5 ml-n1 mr-0">
      <stamp-data-table
        :headers="headers"
        :data="template_types"
        :actions="actions"
        :search="search"
        :is-loading="isTemplateTypesTableLoading"
        :isItemsPerPageDisabled="true"
        item-key="template_type_firebase_id"
        @viewTemplateType="viewTemplateType"
        @editTemplateType="editTemplateType"
        @duplicateTemplateType="duplicateTemplateType"
        @deleteTemplateType="deleteTemplateType"
      >
        <template #[`item.graphic`]="{ item }">
          <v-img max-width="35" :src="item.image"></v-img>
        </template>
        <template #[`item.template_type_firebase_id`]="{ item }">
          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-icon color="amber darken-3" v-on="on" @click="copyItemToClipboard(item.template_type_firebase_id)">mdi-firebase</v-icon>
            </template>
            <span>Copy FirebaseID ( {{ item.template_type_firebase_id }} ) to clipboard</span>
          </v-tooltip>
        </template>
      </stamp-data-table>
    </div>
  </v-container>
</template>

<script>
import ConfirmDeleteDialog from '../../dialog/ConfirmDeleteDialog';
import ConfirmationDialog from '../../order-management/orders/orderDialogs/ConfirmationDialog';
import { mapGetters } from 'vuex';
import DataTable from '@/components/common/DataTable.vue';

export default {
  components: {
    'confirm-delete-dialog': ConfirmDeleteDialog,
    'confirmation-dialog': ConfirmationDialog,
    'stamp-data-table': DataTable
  },
  data() {
    return {
      search: '',
      headers: [
        { text: 'Name', value: 'name', sortable: true },
        { text: 'Firebase ID', value: 'template_type_firebase_id', align: 'center', sortable: false }
      ],
      actions: [
        { icon: 'mdi-eye', iconColor: 'blue darken-1', title: 'View Template Type', eventName: 'viewTemplateType' },
        { icon: 'mdi-pencil', iconColor: 'green darken-2', title: 'Edit Template Type', eventName: 'editTemplateType' },
        { icon: 'mdi-plus-box-multiple-outline', iconColor: 'grey darken-1', title: 'Duplicate Template Type', eventName: 'duplicateTemplateType' }
      ],
      isDeleteDialogOpened: false,
      selectedTemplateType: {},
      isConfirmationDialogOpened: false,
      confirmationDialogAction: ''
    };
  },
  computed: {
    ...mapGetters('template_types', {
      template_types: 'getTemplateTypes',
      isTemplateTypesTableLoading: 'getTemplateTypesLoadingState'
    }),
    ...mapGetters('users', {
      userAccessLevel: 'userAccessLevel'
    }),
    isUserSuperAdmin() {
      return this.userAccessLevel && parseInt(this.userAccessLevel.value) === 100;
    }
  },
  created() {
    this.$store.dispatch('template_types/getAllTemplateTypes');
    if (this.isUserSuperAdmin) {
      this.actions.push({ icon: 'mdi-delete-outline', iconColor: 'red darken-1', title: 'Delete Template Type', eventName: 'deleteTemplateType' });
    }
  },
  methods: {
    addNewTemplateTypeDialog() {
      this.$router.replace('/template-types/new');
    },
    editTemplateType(template_type) {
      this.$router.replace(`/template-types/edit/${template_type.template_type_firebase_id}`);
    },
    viewTemplateType(template_type) {
      this.$router.replace(`/template-types/view/${template_type.template_type_firebase_id}`);
    },
    duplicateTemplateType(template_type) {
      this.confirmationDialogAction = `duplicate template type '${template_type.name}'`;
      this.selectedTemplateType.data = template_type;
      this.isConfirmationDialogOpened = true;
    },
    async duplicateConfirmed() {
      this.$store.dispatch('showLoader', { message: `Duplicating template type ${this.selectedTemplateType.data.name} in progress...` });
      try {
        this.selectedTemplateType.data.name = `${this.selectedTemplateType.data.name} Duplicated`;
        const { template_type_firebase_id, ...typeData } = this.selectedTemplateType.data;
        const templateTypeDuplicate = { data: typeData };
        const result = await this.$store.dispatch('template_types/addNewTemplateType', templateTypeDuplicate);
        this.setSnackbar('success', `Template Type saved with id: ${result}. `);
      } catch (error) {
        this.showAlert('error', error.message || error);
      } finally {
        this.selectedTemplateType = {};
        this.closeConfirmationDialog();
        this.$store.dispatch('hideLoader');
      }
    },
    deleteTemplateType(template_type) {
      this.selectedTemplateType = template_type;
      this.isDeleteDialogOpened = true;
    },
    async deleteConfirmed() {
      this.$store.dispatch('showLoader', { message: `Deleting template type in progress...` });
      try {
        await this.$store.dispatch('template_types/deleteTemplateType', { template_type_firebase_id: this.selectedTemplateType.template_type_firebase_id });
        this.setSnackbar('success', `Template Type [ ${this.selectedTemplateType.template_type_firebase_id} ] has been deleted successfully!`);
      } catch (error) {
        console.error('[deleteTemplateType error]', error);
        this.showAlert('error', error.message || error || 'Could not delete template type!');
      } finally {
        this.closeDeleteDialog();
        this.selectedTemplateType = {};

        this.$store.dispatch('hideLoader');
      }
    },
    closeDeleteDialog() {
      this.isDeleteDialogOpened = false;
    },
    closeConfirmationDialog() {
      this.isConfirmationDialogOpened = false;
      this.confirmationDialogAction = '';
    },
    showAlert(type, message) {
      this.$store.dispatch('showAlert', { type, message });
    },
    setSnackbar(type, message) {
      this.$store.dispatch('setSnackbar', { type, message });
    },
    copyItemToClipboard(item) {
      navigator.clipboard.writeText(item);
    }
  }
};
</script>
